import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import VisionModeBadge from './VisionModeBadge';
import { useTrading } from '../context/TradingContext';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function MonthlyLeaderboard() {
  const { systemStatus } = useTrading();
  const [data, setData] = useState([]);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [availableYears, setAvailableYears] = useState([]);

  // Determine trading status
  const isTradingActive = systemStatus?.is_trading_active && systemStatus?.within_trading_hours;
  const euMarket = systemStatus?.trading_session?.euMarket;
  const usMarket = systemStatus?.trading_session?.usMarket;
  const euMarketOpen = euMarket?.isOpen;
  const usMarketOpen = usMarket?.isOpen;

  // Build tooltip texts for market badges
  const getMarketTooltip = (market, isOpen) => {
    if (!market) return '';
    const hours = `${market.openUTC} - ${market.closeUTC} UTC`;
    if (isOpen) {
      return `Trading active\n${hours}`;
    } else {
      return `Opens at ${market.openUTC} UTC\n${hours}`;
    }
  };

  // Fetch available periods to populate year dropdown
  useEffect(() => {
    async function fetchPeriods() {
      try {
        const response = await fetch('/api/leaderboard/periods');
        const result = await response.json();
        if (result.data && result.data.length > 0) {
          const years = [...new Set(result.data.map(p => p.year))].sort((a, b) => b - a);
          setAvailableYears(years);
        } else {
          // Default to current year if no periods
          setAvailableYears([new Date().getFullYear()]);
        }
      } catch (err) {
        console.error('Error fetching periods:', err);
        setAvailableYears([new Date().getFullYear()]);
      }
    }
    fetchPeriods();
  }, []);

  // Fetch monthly leaderboard data
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/leaderboard/monthly?year=${selectedYear}&month=${selectedMonth}`
        );
        const result = await response.json();
        setData(result.data || []);
        setPeriod(result.period || null);
      } catch (err) {
        console.error('Error fetching monthly leaderboard:', err);
        setError('Failed to load monthly leaderboard');
        setData([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [selectedYear, selectedMonth]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    const formatted = Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    if (value >= 0) return `$${formatted}`;
    return `-$${formatted}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.0%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse text-dark-text-secondary">Loading monthly leaderboard...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Period Selector Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 border-b border-dark-border-subtle">
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Month Selector */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="period-select"
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>

          {/* Year Selector */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="period-select"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Trading Status Badges */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {isTradingActive ? (
            <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-positive/10 border border-positive/30 rounded text-positive text-[10px] sm:text-xs font-medium">
              <span className="w-1.5 h-1.5 bg-positive rounded-full animate-pulse"></span>
              <span className="hidden xs:inline">Trading</span> Active
            </div>
          ) : (
            <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-dark-tertiary border border-dark-border rounded text-dark-text-muted text-[10px] sm:text-xs font-medium">
              <span className="w-1.5 h-1.5 bg-dark-text-muted rounded-full"></span>
              <span className="hidden xs:inline">Markets</span> Closed
            </div>
          )}
          <div
            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded text-[10px] sm:text-xs font-medium cursor-default ${
              euMarketOpen
                ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
                : 'bg-dark-tertiary border border-dark-border text-dark-text-muted'
            }`}
            title={getMarketTooltip(euMarket, euMarketOpen)}
          >
            DAX
          </div>
          <div
            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded text-[10px] sm:text-xs font-medium cursor-default ${
              usMarketOpen
                ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
                : 'bg-dark-tertiary border border-dark-border text-dark-text-muted'
            }`}
            title={getMarketTooltip(usMarket, usMarketOpen)}
          >
            NAS
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="m-4 p-4 bg-negative/10 border border-negative/30 rounded-lg text-negative text-sm">
          {error}
        </div>
      )}

      {/* Empty State */}
      {!error && data.length === 0 && (
        <div className="p-8 text-center text-dark-text-muted">
          <p>No trading data for this period</p>
          <p className="text-sm mt-2">Select a different month or wait for trading activity</p>
        </div>
      )}

      {/* Leaderboard Table */}
      {data.length > 0 && (
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th className="w-10 md:w-[60px]">Rank</th>
              <th>Model</th>
              <th className="hidden sm:table-cell w-16 md:w-[100px]">Mode</th>
              <th className="hidden md:table-cell w-14 md:w-[80px] text-right">Trades</th>
              <th className="hidden lg:table-cell" style={{ width: '90px' }}>Win Rate</th>
              <th className="hidden lg:table-cell" style={{ width: '100px' }}>P/L</th>
              <th className="w-16 md:w-[100px]">Return</th>
              <th className="w-20 md:w-[140px]">Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.map((model) => (
              <tr key={model.model_id}>
                <td><RankBadge rank={model.rank} /></td>
                <td>
                  <Link to={`/model/${model.model_id}`} className="flex flex-col min-w-0 group">
                    <span className="text-xs md:text-sm font-semibold text-dark-text group-hover:text-accent-gold transition-colors truncate">
                      {model.model_name}
                    </span>
                    <span className="text-[10px] md:text-xs text-dark-text-muted">{model.provider}</span>
                  </Link>
                </td>
                <td className="hidden sm:table-cell">
                  <VisionModeBadge mode={model.vision_mode} />
                </td>
                <td className="hidden md:table-cell">
                  <span className="font-mono text-xs md:text-sm font-medium text-dark-text-secondary text-right block">
                    {model.trade_count}
                  </span>
                </td>
                <td className="hidden lg:table-cell">
                  <span className="font-mono text-sm font-medium text-right block">
                    {model.win_rate?.toFixed(1) || 0}%
                  </span>
                </td>
                <td className="hidden lg:table-cell">
                  <span className={`money ${
                    model.total_pnl_usd > 0 ? 'money-positive' :
                    model.total_pnl_usd < 0 ? 'money-negative' : 'money-neutral'
                  }`}>
                    {model.total_pnl_usd >= 0 ? '+' : ''}{formatCurrency(model.total_pnl_usd)}
                  </span>
                </td>
                <td>
                  <span className={`money ${
                    model.return_percent > 0 ? 'money-positive' :
                    model.return_percent < 0 ? 'money-negative' : 'money-neutral'
                  }`}>
                    {formatPercent(model.return_percent)}
                  </span>
                </td>
                <td>
                  <span className="money money-balance">{formatCurrency(model.current_balance)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RankBadge({ rank }) {
  if (rank === null || rank === undefined) {
    return (
      <div className="rank-badge rank-other">
        -
      </div>
    );
  }

  const rankClass = rank <= 3 ? `rank-${rank}` : 'rank-other';
  return (
    <div className={`rank-badge ${rankClass}`}>
      {rank}
    </div>
  );
}

export default MonthlyLeaderboard;
